import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { CatalogTundishShroud } from "../../../models/CatalogTundishShroud";
import { useTableData } from "../hooks/useTableData";
import { useTundishOptionsFunctions } from "../hooks/useTundishShroudOptionsFunctions";
import { TundishShroudModal } from "./TundishShroudModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogTundishShroudsFromAPI: Function;
  isLoading: boolean;
  tundishShroudId: String | undefined;
};

export const TableTundishShroud = ({
  getCatalogTundishShroudsFromAPI,
  isLoading,
  tundishShroudId,
}: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [tundishShroud, setTundishShroud] =
    useState<CatalogTundishShroud | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewTundishShroud = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setTundishShroud(null);
    navigate(`/configuration/caster-tundish-shrouds/new`);
  };

  const { getMenuOptions } = useTundishOptionsFunctions({
    setTundishShroud,
    setShowModal,
    handleNewTundishShroud,
  });
  const { rows, columnDefs } = useTableData({
    setTundishShroud,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setTundishShroud(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setTundishShroud(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/caster-tundish-shrouds/${
          (event.data as CatalogTundishShroud).TundishShroudId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/caster-tundish-shrouds/${
          (event.data as CatalogTundishShroud).TundishShroudId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "tundishShroudContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setTundishShroud(null);
    navigate(`/configuration/caster-tundish-shrouds/`);
  };

  useEffect(() => {
    if (tundishShroudId) {
      if (tundishShroudId === "new") {
        handleNewTundishShroud();
      } else {
        let tundishNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if (
            (n.data as CatalogTundishShroud).TundishShroudId ===
            Number(tundishShroudId)
          ) {
            tundishNode = n;
            return true;
          }
        });

        if (tundishNode!) {
          const pageSize = gridApi?.paginationGetPageSize();
          const pageNumber = Math.floor(
            (tundishNode!.rowIndex ?? 0) / pageSize!,
          );
          gridApi?.paginationGoToPage(pageNumber);

          tundishNode.setSelected(true);
          setTundishShroud(tundishNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "tundishShroudContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Tundish Shrouds
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogTundishShroud>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              loading={isLoading}
              getRowId={(params) => String(params.data.TundishShroudId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton
            label="TUNDISH SHROUD"
            onClick={handleNewTundishShroud}
            sizes={{ md: 3 }}
          />
        </Grid>
      </Grid>
      <TundishShroudModal
        tundishShroud={tundishShroud}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogTundishShroudsFromAPI}
      />
    </>
  );
};
